import { useRef, useState, type ChangeEvent, type ClipboardEvent, type KeyboardEvent } from 'react'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { useTranslation } from 'react-i18next'
import { respondentBaseService, type RespondentTag } from '@/services/respondentBaseService'
import GroupMenu, { Dot } from './GroupMenu'
import { hueFor, isEmail, splitAddresses } from './visuals'

type Token = { email: string; valid: boolean }

type Props = {
  groups: RespondentTag[]
  onAdded: (summary: string) => void | Promise<void>
  onError: (message: string) => void
  /** Empty-state hero: taller field, focused on mount. */
  large?: boolean
}

/**
 * One field for one address or a whole column: text turns into tokens as it is
 * pasted or typed, typos are flagged before anything is sent.
 */
export default function PasteComposer({ groups, onAdded, onError, large = false }: Props) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('respondents')
  const inputRef = useRef<HTMLInputElement>(null)

  const [tokens, setTokens] = useState<Token[]>([])
  const [draft, setDraft] = useState('')
  const [group, setGroup] = useState<string | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [focused, setFocused] = useState(false)
  const [saving, setSaving] = useState(false)

  const commit = (text: string) => {
    const incoming = splitAddresses(text)
    if (incoming.length === 0) return
    setTokens((current) => {
      const seen = new Set(current.map((tk) => tk.email))
      const next = [...current]
      for (const email of incoming) {
        if (seen.has(email)) continue
        seen.add(email)
        next.push({ email, valid: isEmail(email) })
      }
      return next
    })
    setDraft('')
  }

  const valid = tokens.filter((tk) => tk.valid)
  const invalidCount = tokens.length - valid.length

  const submit = async () => {
    const pending = draft.trim() ? [...tokens, ...splitAddresses(draft).map((email) => ({ email, valid: isEmail(email) }))] : tokens
    const emails = pending.filter((tk) => tk.valid).map((tk) => tk.email)
    if (emails.length === 0) return
    setSaving(true)
    try {
      const res = await respondentBaseService.bulkAdd({
        emails: emails.join('\n'),
        tag_names: group ? [group] : [],
      })
      // Keep what the server refused next to the typos, so both can be fixed in place.
      const refused = new Set(res.invalid)
      setTokens(pending.filter((tk) => !tk.valid || refused.has(tk.email)).map((tk) => ({ ...tk, valid: false })))
      setDraft('')
      const parts = [t('composer.result', { created: res.created, updated: res.updated })]
      if (res.invalid.length) parts.push(t('composer.skipped', { count: res.invalid.length }))
      await onAdded(parts.join(' '))
    } catch (err) {
      onError(err instanceof Error ? err.message : t('errors.save'))
    } finally {
      setSaving(false)
    }
  }

  const editToken = (index: number) => {
    const token = tokens[index]
    setTokens((current) => current.filter((_, i) => i !== index))
    setDraft(token.email)
    inputRef.current?.focus()
  }

  const groupMeta = groups.find((g) => g.name === group)
  const canSubmit = valid.length > 0 || isEmail(draft.trim().toLowerCase())
  const addCount = valid.length + (isEmail(draft.trim().toLowerCase()) ? 1 : 0)

  return (
    <Box>
      <Box
        onClick={() => inputRef.current?.focus()}
        sx={{
          display: 'flex',
          alignItems: large ? 'flex-start' : 'center',
          flexDirection: { xs: 'column', sm: large ? 'column' : 'row' },
          gap: 1.25,
          p: large ? 1.5 : '8px 8px 8px 14px',
          bgcolor: 'background.paper',
          borderRadius: '16px',
          cursor: 'text',
          boxShadow: focused
            ? `0 0 0 2px ${c.coral}, 0 0 0 6px ${alpha(c.coral, 0.12)}`
            : `0 0 0 1px ${c.ink08}, 0 1px 2px ${c.ink04}`,
          transition: 'box-shadow 120ms ease-out',
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: '100%',
            minHeight: large ? 96 : 36,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: large ? 'flex-start' : 'center',
            alignContent: 'flex-start',
            gap: 0.75,
          }}
        >
          {tokens.map((tk, i) => (
            <Box
              key={tk.email}
              component="span"
              onClick={(e) => {
                e.stopPropagation()
                if (!tk.valid) editToken(i)
              }}
              sx={{
                height: 28,
                pl: 1.25,
                pr: 0.5,
                borderRadius: '8px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                fontSize: 13,
                bgcolor: tk.valid ? c.ink04 : c.coralSoft,
                color: tk.valid ? c.ink : c.coralHover,
                textDecoration: tk.valid ? 'none' : `wavy underline ${alpha(c.coralHover, 0.5)}`,
                cursor: tk.valid ? 'default' : 'pointer',
              }}
            >
              {tk.valid && <Dot color={c.success} size={6} />}
              {tk.email}
              <Box
                component="button"
                type="button"
                aria-label="remove"
                onClick={(e) => {
                  e.stopPropagation()
                  setTokens((current) => current.filter((_, j) => j !== i))
                }}
                sx={{ border: 0, bgcolor: 'transparent', p: 0.25, display: 'grid', color: 'inherit', opacity: 0.5, cursor: 'pointer', '&:hover': { opacity: 1 } }}
              >
                <CloseRoundedIcon sx={{ fontSize: 14 }} />
              </Box>
            </Box>
          ))}
          <Box
            component="input"
            ref={inputRef}
            value={draft}
            autoFocus={large}
            placeholder={tokens.length ? t('composer.placeholderMore') : t('composer.placeholder')}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false)
              if (draft.trim()) commit(draft)
            }}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value
              // A separator typed at the end closes the current address.
              if (/[\s,;]$/.test(value)) commit(value)
              else setDraft(value)
            }}
            onPaste={(e: ClipboardEvent<HTMLInputElement>) => {
              e.preventDefault()
              commit(draft + ' ' + e.clipboardData.getData('text'))
            }}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                if (draft.trim()) commit(draft)
                else if (valid.length) void submit()
              } else if (e.key === 'Backspace' && !draft && tokens.length) {
                setTokens((current) => current.slice(0, -1))
              }
            }}
            sx={{
              flex: 1,
              minWidth: 180,
              height: 28,
              border: 0,
              outline: 0,
              bgcolor: 'transparent',
              font: 'inherit',
              fontSize: 14,
              color: c.ink,
              '&::placeholder': { color: c.ink48 },
            }}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            alignSelf: { xs: 'stretch', sm: large ? 'stretch' : 'center' },
            justifyContent: 'flex-end',
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setMenuAnchor(e.currentTarget)
            }}
            sx={{
              height: 36,
              px: 1.5,
              border: 0,
              borderRadius: '10px',
              bgcolor: c.ink04,
              color: c.ink72,
              font: 'inherit',
              fontSize: 13,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              mr: large ? 'auto' : 0,
              '&:hover': { bgcolor: c.ink08 },
            }}
          >
            <Dot color={groupMeta ? hueFor(groupMeta.id) : group ? c.coral : c.ink32} />
            {t('composer.to', { group: group ?? t('composer.noGroup') })} ▾
          </Box>
          <Button
            variant="contained"
            size="small"
            disabled={!canSubmit || saving}
            onClick={(e) => {
              e.stopPropagation()
              void submit()
            }}
            sx={{ height: 36, px: 2, borderRadius: '999px', fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            {saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : addCount > 0 ? (
              t('composer.add', { count: addCount })
            ) : (
              t('composer.addEmpty')
            )}
          </Button>
        </Box>
      </Box>
      {invalidCount > 0 && (
        <Typography sx={{ fontSize: 12.5, color: c.coralHover, mt: 0.75, ml: 1.5 }}>{t('composer.invalid')}</Typography>
      )}

      <GroupMenu anchorEl={menuAnchor} onClose={() => setMenuAnchor(null)} groups={groups} onPick={setGroup} />
    </Box>
  )
}
